import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Business as BusinessIcon,
  CloudUpload as CloudUploadIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Sync as SyncIcon,
  Link as LinkIcon
} from '@mui/icons-material';

const CRMIntegration = ({ business, websiteAnalysis, prospects }) => {
  const [selectedCRM, setSelectedCRM] = useState('hubspot');
  const [apiKey, setApiKey] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [selectedData, setSelectedData] = useState({
    businessInfo: true,
    websiteAnalysis: true,
    auditResults: true,
    proposals: true,
    prospects: true
  });

  const crmOptions = [
    {
      value: 'hubspot',
      label: 'HubSpot',
      description: 'CRM et marketing automation',
      icon: '🟠',
      fields: ['contact', 'company', 'deal', 'task']
    },
    {
      value: 'salesforce',
      label: 'Salesforce',
      description: 'CRM enterprise',
      icon: '🔵',
      fields: ['lead', 'account', 'opportunity', 'task']
    },
    {
      value: 'pipedrive',
      label: 'Pipedrive',
      description: 'CRM simple et efficace',
      icon: '🟢',
      fields: ['person', 'organization', 'deal', 'activity']
    },
    {
      value: 'zoho',
      label: 'Zoho CRM',
      description: 'CRM complet',
      icon: '🟣',
      fields: ['lead', 'account', 'deal', 'task']
    },
    {
      value: 'freshsales',
      label: 'Freshsales',
      description: 'CRM moderne',
      icon: '🟡',
      fields: ['contact', 'account', 'deal', 'task']
    }
  ];

  const selectedCRMOption = crmOptions.find(crm => crm.value === selectedCRM);

  const testConnection = async () => {
    if (!apiKey.trim()) {
      setSyncStatus({ type: 'error', message: 'Veuillez saisir une clé API' });
      return;
    }

    setIsSyncing(true);
    setSyncStatus({ type: 'info', message: 'Test de connexion en cours...' });

    try {
      // Simulation de test de connexion
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simuler un succès ou échec
      const success = Math.random() > 0.3;
      
      if (success) {
        setIsConnected(true);
        setSyncStatus({ type: 'success', message: 'Connexion réussie !' });
      } else {
        setSyncStatus({ type: 'error', message: 'Échec de la connexion. Vérifiez votre clé API.' });
      }
    } catch (error) {
      setSyncStatus({ type: 'error', message: 'Erreur de connexion: ' + error.message });
    } finally {
      setIsSyncing(false);
    }
  };

  const syncData = async () => {
    if (!isConnected) {
      setSyncStatus({ type: 'error', message: 'Veuillez d\'abord vous connecter au CRM' });
      return;
    }

    setIsSyncing(true);
    setSyncStatus({ type: 'info', message: 'Synchronisation en cours...' });

    try {
      // Simulation de synchronisation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const syncedData = [];
      
      if (selectedData.businessInfo && business) {
        syncedData.push('Informations entreprise');
      }
      
      if (selectedData.websiteAnalysis && websiteAnalysis) {
        syncedData.push('Analyse technique');
      }
      
      if (selectedData.auditResults) {
        syncedData.push('Résultats d\'audit');
      }
      
      if (selectedData.proposals) {
        syncedData.push('Propositions commerciales');
      }
      
      if (selectedData.prospects && prospects) {
        syncedData.push(`${prospects.length} prospects`);
      }

      setSyncStatus({ 
        type: 'success', 
        message: `Synchronisation réussie ! ${syncedData.length} élément(s) envoyé(s) vers ${selectedCRMOption.label}` 
      });
    } catch (error) {
      setSyncStatus({ type: 'error', message: 'Erreur de synchronisation: ' + error.message });
    } finally {
      setIsSyncing(false);
    }
  };

  const generateCRMData = () => {
    const data = {};

    if (selectedData.businessInfo && business) {
      data.businessInfo = {
        name: business.name,
        website: business.website,
        phone: business.phone,
        email: business.email,
        address: business.address,
        type: business.type
      };
    }

    if (selectedData.websiteAnalysis && websiteAnalysis) {
      data.websiteAnalysis = {
        overallScore: websiteAnalysis.overallScore,
        performance: websiteAnalysis.performance.performanceScore,
        security: websiteAnalysis.security.securityScore,
        seo: websiteAnalysis.seo.seoScore,
        mobile: websiteAnalysis.mobile.mobileScore,
        criticalIssues: websiteAnalysis.criticalIssues,
        estimatedBudget: websiteAnalysis.estimatedBudget
      };
    }

    if (selectedData.prospects && prospects) {
      data.prospects = prospects.map(prospect => ({
        name: prospect.name,
        website: prospect.website,
        phone: prospect.phone,
        email: prospect.email,
        status: prospect.status,
        priority: prospect.priority,
        score: prospect.websiteAnalysis?.overallScore || 0
      }));
    }

    return data;
  };

  const downloadCRMData = () => {
    const data = generateCRMData();
    const content = JSON.stringify(data, null, 2);
    
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crm-data-${business?.name || 'prospects'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon color="primary" />
        Intégration CRM
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Synchronisez vos données de prospection et d'analyse avec votre CRM pour optimiser votre workflow.
      </Typography>

      {/* Sélection du CRM */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Choisir votre CRM
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>CRM</InputLabel>
            <Select
              value={selectedCRM}
              onChange={(e) => setSelectedCRM(e.target.value)}
              label="CRM"
            >
              {crmOptions.map(crm => (
                <MenuItem key={crm.value} value={crm.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{crm.icon}</span>
                    <Box>
                      <Typography variant="subtitle1">{crm.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {crm.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedCRMOption && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Champs supportés :
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedCRMOption.fields.map(field => (
                  <Chip key={field} label={field} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Configuration API */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration API
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Clé API"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Saisissez votre clé API"
                type="password"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={testConnection}
                disabled={isSyncing || !apiKey.trim()}
              >
                {isSyncing ? 'Test...' : 'Tester Connexion'}
              </Button>
            </Grid>
          </Grid>

          {isConnected && (
            <Alert severity="success" sx={{ mt: 2 }}>
              ✅ Connecté à {selectedCRMOption.label}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Sélection des données */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Données à synchroniser
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedData.businessInfo}
                    onChange={(e) => setSelectedData({...selectedData, businessInfo: e.target.checked})}
                  />
                }
                label="Informations entreprise"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedData.websiteAnalysis}
                    onChange={(e) => setSelectedData({...selectedData, websiteAnalysis: e.target.checked})}
                  />
                }
                label="Analyse technique"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedData.auditResults}
                    onChange={(e) => setSelectedData({...selectedData, auditResults: e.target.checked})}
                  />
                }
                label="Résultats d'audit"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedData.proposals}
                    onChange={(e) => setSelectedData({...selectedData, proposals: e.target.checked})}
                  />
                }
                label="Propositions commerciales"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedData.prospects}
                    onChange={(e) => setSelectedData({...selectedData, prospects: e.target.checked})}
                  />
                }
                label={`Prospects (${prospects?.length || 0})`}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={syncData}
              disabled={!isConnected || isSyncing}
              size="large"
            >
              {isSyncing ? 'Synchronisation...' : 'Synchroniser avec CRM'}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadCRMData}
              size="large"
            >
              Télécharger Données
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Statut de synchronisation */}
      {syncStatus && (
        <Alert severity={syncStatus.type} sx={{ mb: 3 }}>
          {syncStatus.message}
        </Alert>
      )}

      {/* Aperçu des données */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Aperçu des Données
          </Typography>
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
            <pre style={{ margin: 0, fontSize: '12px' }}>
              {JSON.stringify(generateCRMData(), null, 2)}
            </pre>
          </Box>
        </CardContent>
      </Card>

      {/* Conseils d'intégration */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            💡 Conseils d'Intégration
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Configurez des workflows automatiques" 
                secondary="Créez des tâches automatiques basées sur les scores d'analyse"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Utilisez les scores pour prioriser" 
                secondary="Triez vos prospects par score technique pour optimiser vos efforts"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WarningIcon color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="Surveillez les alertes" 
                secondary="Configurez des alertes pour les problèmes critiques détectés"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SyncIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Synchronisez régulièrement" 
                secondary="Mettez à jour vos données CRM après chaque analyse"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CRMIntegration;